import { defineStore } from "pinia";
import { generateCacheKey, clearCache } from "@/composables/useCache";
import { store } from "@/stores/global.js";
import { doFetch } from "@/composables/doFetch";

export const useChartDataStore = defineStore("chartData", {
    state: () => ({
        chartData: {},
        isLoading: false,
        lastFetchTime: {},
        fetchInProgress: {},
        cacheTimeMs: 300000, // 5 minute cache
    }),

    getters: {
        /**
         * Get chart data for a specific symbol
         */
        getChartData:
            state =>
            (symbol, params = {}) => {
                if (!symbol) return null;
                const normalizedSymbol = symbol.trim().toUpperCase();
                const cacheKey = generateCacheKey(normalizedSymbol, params);
                return state.chartData[cacheKey] || null;
            },

        /**
         * Format chart data for Highcharts for a specific symbol
         */
        getFormattedChartData:
            state =>
            (symbol, params = {}) => {
                if (!symbol) return { series: [] };

                const normalizedSymbol = symbol.trim().toUpperCase();
                const cacheKey = generateCacheKey(normalizedSymbol, params);
                const data = state.chartData[cacheKey];

                if (!data?.intraday?.[0]?.interval) {
                    return { series: [] };
                }

                try {
                    // Extract the price data for the chart
                    const intervalData = data.intraday[0].interval; // Enhanced filtering to handle NaN and invalid values
                    let validPoints = intervalData
                        .filter(
                            item =>
                                // Check for presence of required properties (only close is mandatory)
                                item.close !== undefined &&
                                item.startdatetime &&
                                // Add explicit check for non-numeric and NaN values (only close is mandatory)
                                !isNaN(parseFloat(item.close)) &&
                                // Include PRE and MKT sessions, but not POST sessions for chart display
                                (item.type === "MKT" || item.type === "PRE")
                        )
                        .map(item => {
                            // Convert ISO date to timestamp if needed
                            const timestamp =
                                typeof item.startdatetime === "string"
                                    ? new Date(item.startdatetime).getTime()
                                    : item.startdatetime; // Convert values to numbers with protection against NaN
                            const close = Number(item.close);
                            const open = !isNaN(Number(item.open)) ? Number(item.open) : close;
                            const high = !isNaN(Number(item.high)) ? Number(item.high) : close;
                            const low = !isNaN(Number(item.low)) ? Number(item.low) : close;

                            return {
                                timestamp,
                                open,
                                close,
                                high,
                                low,
                                // Extract date part for session grouping (YYYY-MM-DD)
                                sessionDate:
                                    typeof item.startdatetime === "string"
                                        ? item.startdatetime.split("T")[0]
                                        : new Date(item.startdatetime).toISOString().split("T")[0],
                                // Include session type for reference but we'll combine all types
                                sessionType: item.type || "MKT",
                                raw: item,
                            };
                        }) // Second filter pass to remove any points with invalid close price
                        .filter(
                            point => !isNaN(point.timestamp) && !isNaN(point.close)
                            // Note: open, high, low are now guaranteed to be valid since they fallback to close
                        )
                        .sort((a, b) => a.timestamp - b.timestamp); // Sort by timestamp asc

                    // If we have no valid data, return empty series
                    if (validPoints.length === 0) {
                        return { series: [] };
                    }

                    // Group points by trading date ONLY (combine all session types for same day)
                    const sessionGroups = {};
                    validPoints.forEach(point => {
                        const key = point.sessionDate; // Use just the date as key (not session type)
                        if (!sessionGroups[key]) {
                            sessionGroups[key] = {
                                date: point.sessionDate,
                                points: [],
                                firstTimestamp: point.timestamp,
                                lastTimestamp: point.timestamp,
                            };
                        }
                        sessionGroups[key].points.push(point);
                        // Update first/last timestamps for this day
                        sessionGroups[key].firstTimestamp = Math.min(
                            sessionGroups[key].firstTimestamp,
                            point.timestamp
                        );
                        sessionGroups[key].lastTimestamp = Math.max(
                            sessionGroups[key].lastTimestamp,
                            point.timestamp
                        );
                    });

                    // Convert to array for easier sorting
                    const sessionDays = Object.values(sessionGroups);

                    // Sort by date (descending)
                    sessionDays.sort((a, b) => {
                        return b.date.localeCompare(a.date); // newest first
                    });

                    // Always select the most recent day with data
                    const selectedDay = sessionDays[0];

                    // Get today's date in YYYY-MM-DD format
                    const today = new Date().toISOString().split("T")[0];

                    // Is the selected day today?
                    const isToday = selectedDay.date === today;

                    // Get all points for the selected day
                    const displayData = selectedDay.points;

                    // Sort the day's points by timestamp
                    displayData.sort((a, b) => a.timestamp - b.timestamp);

                    // Get the first price of the day (first interval's opening price)
                    const firstPoint = displayData[0];
                    const dayOpenPrice = firstPoint.open; // First interval's open price
                    const dayStartTime = firstPoint.timestamp;

                    // Get the most recent price (last interval's closing price)
                    const lastPoint = displayData[displayData.length - 1];
                    const dayLastPrice = lastPoint.close;

                    // Format the points for Highcharts: [timestamp, close]
                    let chartData = [];

                    // We want to ensure we have all trading data, starting with the day's open price
                    // Use the first interval's timestamp but with its opening price
                    chartData.push([dayStartTime, dayOpenPrice]);

                    // Then add all the regular interval data points with their closing prices
                    chartData = chartData.concat(
                        displayData.map(point => [point.timestamp, point.close])
                    );

                    // For session type, use the most recent point's session type
                    const mostRecentSessionType = displayData[displayData.length - 1].sessionType;

                    return {
                        series: [
                            {
                                data: chartData,
                                name: normalizedSymbol,
                            },
                        ],
                        sessionDate: selectedDay.date,
                        sessionType: mostRecentSessionType,
                        isToday: isToday,
                        pointCount: chartData.length,
                        openingPrice: dayOpenPrice,
                        lastPrice: dayLastPrice,
                    };
                } catch (error) {
                    console.error(`Error formatting chart data for ${normalizedSymbol}:`, error);
                    return { series: [] };
                }
            },
    },
    actions: {
        /**
         * Direct API call to fetch chart data
         * @param {Object} params - API parameters
         * @param {string} token - Authentication token
         * @returns {Promise<Object>} - API response
         */
        async callChartDataAPI(params, token) {
            const url = new URL(
                `https://${store.env}.quotemedia.com/datatool/getEnhancedChartData.json`
            );

            url.searchParams.append("webmaster_id", store.wmid);
            url.searchParams.append("webmasterId", store.wmid);
            url.searchParams.append("qmodTool", "MiniQuotes"); // Use component name as tool
            url.searchParams.append("pathName", window.location.pathname);
            if (store.sid) url.searchParams.append("sid", store.sid);

            // Add all the parameters
            for (const key in params) {
                if (params[key] != null) {
                    url.searchParams.append(key, params[key]);
                }
            }

            return await doFetch(url, {
                method: "GET",
                headers: {
                    "Accept-Language": "en",
                    "Content-Type": "application/json",
                    "Datatool-Token": token,
                },
            });
        },

        /**
         * Fetch chart data for the provided symbol
         * @param {string} symbol - Symbol to fetch chart data for
         * @param {string} token - Authentication token for the API call
         * @param {Object} params - Additional parameters for the chart request
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Object>} - Chart data
         */
        async fetchChartData(symbol, token, params = {}, force = false) {
            if (!symbol) return null;
            if (!token) throw new Error("Token is required for API calls");

            // Normalize symbol
            const normalizedSymbol = symbol.trim().toUpperCase(); // Create a clean params object with only the relevant properties to ensure consistent cache keys
            const cleanParams = {
                interval: params.interval || 30,
                marketSession: params.marketSession || "mkt", // Default to include all sessions if not specified
            };

            // Generate cache key with both symbol and cleaned params
            const cacheKey = generateCacheKey(normalizedSymbol, cleanParams);

            // If already fetching this symbol with these params, wait for that request
            if (this.fetchInProgress[cacheKey]) {
                return this.fetchInProgress[cacheKey];
            }

            // Check if we need to fetch (not cached or force refresh)
            const now = Date.now();
            const needsFetch =
                force ||
                !this.chartData[cacheKey] ||
                !this.lastFetchTime[cacheKey] ||
                now - this.lastFetchTime[cacheKey] > this.cacheTimeMs;

            if (!needsFetch) {
                return this.chartData[cacheKey];
            }

            // Calculate a better start date to ensure we have data
            // Get date 5 days ago to have enough buffer for weekends and holidays
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(today.getDate() - 5); // Go back 5 days to ensure we have previous day data

            const formattedStartDate = startDate.toISOString().split("T")[0]; // Default parameters for intraday chart
            const defaultParams = {
                symbol: normalizedSymbol,
                start: formattedStartDate,
                zeroTradeDays: false,
                marketSession: cleanParams.marketSession, // Use the cleaned parameter value
                freq: "day",
                adjusted: true,
                adjType: "splitsanddivs",
                unadjusted: false,
                datatype: "int",
                interval: cleanParams.interval,
            };

            // Merge with custom parameters
            const chartParams = { ...defaultParams, ...params };
            const fetchPromise = (async () => {
                try {
                    this.isLoading = true;

                    // Fetch the chart data
                    const response = await this.callChartDataAPI(chartParams, token);

                    // Handle error response from doFetch
                    if (response.error) {
                        throw new Error(`API call failed: ${response.error}`);
                    }

                    // Check if the response contains errors
                    if (
                        response &&
                        (response.error || (response.results && response.results.errors))
                    ) {
                        const errorDetails =
                            response.error || (response.results && response.results.errors);
                        console.warn(
                            `Chart data for ${normalizedSymbol} contains errors:`,
                            errorDetails
                        );
                        throw new Error(`API returned errors: ${JSON.stringify(errorDetails)}`);
                    }

                    // Update the store with new data
                    this.chartData[cacheKey] = response.results || response;
                    this.lastFetchTime[cacheKey] = now;

                    return response.results || response;
                } catch (error) {
                    console.warn(`Failed to fetch chart data for ${normalizedSymbol}:`, error);
                    return null;
                } finally {
                    this.isLoading = false;
                    delete this.fetchInProgress[cacheKey];
                }
            })();

            this.fetchInProgress[cacheKey] = fetchPromise;
            return fetchPromise;
        } /**
         * Fetch chart data for multiple symbols
         * @param {string|string[]} symbols - Symbol or array of symbols to fetch
         * @param {string} token - Authentication token for the API call
         * @param {Object} params - Additional parameters for the chart request
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Object>} - Object containing chart data by symbol
         */,
        async fetchMultipleChartData(symbols, token, params = {}, force = false) {
            if (!symbols) return {};
            if (!token) throw new Error("Token is required for API calls");

            // Normalize symbols to array
            const symbolsArray = Array.isArray(symbols)
                ? symbols.map(s => s.trim().toUpperCase())
                : symbols.split(",").map(s => s.trim().toUpperCase());
            const cleanParams = {
                interval: params.interval || 30,
                marketSession: params.marketSession || "mkt", // Use the parameter or default to all sessions
            };

            // Fetch chart data for each symbol
            const promises = symbolsArray.map(symbol =>
                this.fetchChartData(symbol, token, cleanParams, force)
            );

            await Promise.all(promises);

            // Return chart data for all symbols
            const result = {};
            for (const symbol of symbolsArray) {
                const cacheKey = generateCacheKey(symbol, cleanParams);
                result[symbol] = this.chartData[cacheKey];
            }

            return result;
        },

        /**
         * Clear cached data for specific symbols or all symbols
         * @param {string|string[]|null} symbols - Symbols to clear or null for all
         * @param {Object|null} params - Parameters to match or null to match any parameters
         */
        clearCache(symbols = null, params = null) {
            // Create filters object based on parameters
            const filters = {};
            if (symbols !== null) {
                filters[0] = Array.isArray(symbols) ? symbols : symbols.split(",");
            }
            if (params !== null) {
                filters[1] = params;
            }

            clearCache(
                {
                    dataCache: this.chartData,
                    timeCache: this.lastFetchTime,
                },
                {
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );
        },
    },
});
